import React, { useState } from 'react';
import { IconButton, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAtomValue, useSetAtom } from 'jotai';
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import CallEndRoundedIcon from '@mui/icons-material/CallEndRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import {
  activityTypesAtom,
  selectedCustomerIdAtom,
  additionalCustomerDataAtom,
  openSnackbarAdviseAtom,
} from '../../../Atoms';
import useAPI from '../../../api';
import useTranslate from '../../../Hooks/useTranslate';
import capitalize from '../../../Helpers/StringsFormatting/capitalize';
import { ColumnFlexBox } from '../../FlexBox';
import DbeAvatar from '../../DbeAvatar';
import {
  PRIORITIES_ACTIVITIES_ICONS,
  PRIORITIES_ACTIVITIES_COLORS,
} from '../../../Pages/PrioritiesPage/Utils/prioritiesActivities';

/**
 * PriorityCard — a single priority row (see MOBILE_PLAN.md §F(2)). Mobile take
 * on the desktop ActivityEntryItem: activity-icon avatar, SMS note preview, a
 * left accent bar for the urgency group, and an icon action — Manage (open the
 * customer) when a contact exists, otherwise Call / Drop a contactless call.
 */
const PriorityCard = function PriorityCard({
  data,
  accentColor = 'transparent',
}) {
  const theme = useTheme();
  const translate = useTranslate();
  const API = useAPI();
  const activityTypes = useAtomValue(activityTypesAtom);
  const setCustomerId = useSetAtom(selectedCustomerIdAtom);
  const setAdditionalCustomerData = useSetAtom(additionalCustomerDataAtom);
  const openSnackbar = useSetAtom(openSnackbarAdviseAtom);
  const [participantId, setParticipantId] = useState(null);

  const activityName = activityTypes?.find(
    (type) => type.id === data?.activityType
  )?.name;
  const isSms = activityName === 'SMS';
  const Icon =
    PRIORITIES_ACTIVITIES_ICONS[
      activityName === 'CALL' ? 'RECALL' : activityName
    ];
  const iconColor = PRIORITIES_ACTIVITIES_COLORS[activityName];
  const secondary = isSms ? data?.activityNote : data?.phoneNumber;

  const openCustomer = () => {
    setCustomerId(data?.contactId);
    setAdditionalCustomerData({
      customerName: data?.fullName,
      parentId: data?.activityId,
      activityTypeId: data?.activityType,
      outcomesFilter: 'RECALL',
      sectionId: 'PRIORITY',
      branchId: data?.assignedBranchId ?? undefined,
    });
  };

  const makeCall = () => {
    API.makeCallWithoutContact(
      data?.phoneNumber,
      (res) => setParticipantId(res),
      () =>
        openSnackbar({
          message: translate('CALLSTART_ERROR'),
          severity: 'error',
        })
    );
  };

  const dropCall = () => {
    API.dropCallWithoutContact(
      participantId,
      () => setParticipantId(null),
      () =>
        openSnackbar({
          message: translate('CALLDROP_ERROR'),
          severity: 'error',
        })
    );
  };

  const renderAction = () => {
    if (data?.contactId) {
      return (
        <IconButton
          aria-label={translate('MANAGE')}
          onClick={openCustomer}
          sx={{
            flex: 'none',
            width: 44,
            height: 44,
            color: 'primary.main',
            bgcolor: theme.custom.mobile.activePill,
          }}
        >
          <ChevronRightRoundedIcon />
        </IconButton>
      );
    }
    if (participantId) {
      return (
        <IconButton
          aria-label={translate('DROP_CALL')}
          onClick={dropCall}
          sx={{
            flex: 'none',
            width: 44,
            height: 44,
            color: '#fff',
            bgcolor: 'error.main',
            '&:hover': { bgcolor: 'error.main' },
          }}
        >
          <CallEndRoundedIcon />
        </IconButton>
      );
    }
    return (
      <IconButton
        aria-label={translate('CALL_CONTACT')}
        onClick={makeCall}
        sx={{
          flex: 'none',
          width: 44,
          height: 44,
          color: '#fff',
          background: theme.custom.mobile.brandGradient,
          '&:hover': { background: theme.custom.mobile.brandGradient },
        }}
      >
        <LocalPhoneRoundedIcon />
      </IconButton>
    );
  };

  return (
    <Paper
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        borderLeft: `5px solid ${accentColor}`,
      }}
    >
      <DbeAvatar
        color={iconColor}
        icon={Icon ? <Icon sx={{ color: 'inherit' }} /> : null}
      />
      <ColumnFlexBox sx={{ flex: 1, minWidth: 0, gap: 0.25 }}>
        <Typography sx={{ fontWeight: 600 }} noWrap>
          {capitalize(data?.fullName) || translate('CONTACT_UNAVAILABLE')}
        </Typography>
        {secondary ? (
          <Typography
            variant="body2"
            noWrap
            sx={{ color: theme.custom.mobile.mutedColor }}
          >
            {secondary}
          </Typography>
        ) : null}
      </ColumnFlexBox>
      {renderAction()}
    </Paper>
  );
};

export default PriorityCard;
